import { useEffect, useState, useRef, forwardRef } from "react";
import styles from "./BackgroundFilter.module.css";

const BackgroundFilter = forwardRef(
  ({ localStream, localVideo, pc, isExpanded, onClose }, ref) => {
    const [backgroundFilter, setBackgroundFilter] = useState("none");
    const canvasRef = useRef(null);
    const ctxRef = useRef(null);
    const videoSourceRef = useRef(null);
    const animationFrameRef = useRef(null);
    const streamCleanupRef = useRef(null);
    const originalStreamRef = useRef(null);
    const isProcessingActiveRef = useRef(false);

    // Handle component unmount - cleanup all resources
    useEffect(() => {
      return () => {
        cleanupResources();
      };
    }, []);

    // Re-apply filter when component visibility changes
    useEffect(() => {
      // If we have an active filter and the component becomes visible again,
      // make sure the filter is actually applied
      if (
        isExpanded &&
        backgroundFilter !== "none" &&
        !isProcessingActiveRef.current
      ) {
        applyFilter(backgroundFilter);
      }
    }, [isExpanded]);

    // Clean up all resources
    const cleanupResources = () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      if (streamCleanupRef.current) {
        try {
          const tracks = streamCleanupRef.current.getTracks();
          if (tracks && tracks.length > 0) {
            tracks.forEach((track) => {
              if (track.kind === "video") {
                track.stop();
              }
            });
          }
        } catch (e) {
          console.log("Error stopping tracks during cleanup:", e);
        }
        streamCleanupRef.current = null;
      }

      if (videoSourceRef.current) {
        videoSourceRef.current.srcObject = null;
        videoSourceRef.current = null;
      }

      isProcessingActiveRef.current = false;
    };

    // Save original camera stream for reuse
    useEffect(() => {
      if (localStream && !originalStreamRef.current) {
        // Create a clone of the original stream to keep as reference
        const originalTracks = localStream.getTracks();
        const clonedStream = new MediaStream();

        originalTracks.forEach((track) => {
          // We don't actually clone the tracks here, just keep a reference to the original stream
          clonedStream.addTrack(track);
        });

        originalStreamRef.current = clonedStream;
      }
    }, [localStream]);

    // Apply the selected filter to the video stream
    const applyFilter = async (filterType) => {
      try {
        // If trying to apply the same filter that's already active, do nothing
        if (backgroundFilter === filterType) return;

        // Set state first so UI reflects change immediately
        setBackgroundFilter(filterType);

        // If we're restoring original camera, handle separately
        if (filterType === "none") {
          await restoreOriginalCamera();
          return;
        }

        // Validate we have the required elements
        if (!localStream) {
          console.error("No local stream available");
          return;
        }

        // SIMPLIFICATION: Get the original video track from our reference
        // This ensures we're always starting with the camera feed, not a processed track
        let sourceVideoTrack;

        if (!originalStreamRef.current) {
          // If we don't have the original stream saved, get it from current local stream
          sourceVideoTrack = localStream.getVideoTracks()[0];
          if (!sourceVideoTrack) {
            console.error("No video track in local stream");
            return;
          }

          // Save this stream for future reference
          const originalStream = new MediaStream([sourceVideoTrack]);
          localStream.getAudioTracks().forEach((track) => {
            originalStream.addTrack(track);
          });
          originalStreamRef.current = originalStream;
        } else {
          // Use the source track from our saved reference
          sourceVideoTrack = originalStreamRef.current.getVideoTracks()[0];
          if (!sourceVideoTrack) {
            // Fall back to getting a fresh camera if our reference is invalid
            console.log(
              "Original stream reference invalid, getting fresh camera"
            );
            await restoreOriginalCamera();
            sourceVideoTrack = localStream.getVideoTracks()[0];
          }
        }

        // Clean up any existing processing resources
        cleanupResources();

        // Create hidden video element for source
        const videoElement = document.createElement("video");
        videoElement.autoplay = true;
        videoElement.playsInline = true;
        videoElement.muted = true;
        videoSourceRef.current = videoElement;

        // Create a dedicated source stream just for our processing pipeline
        // Using the original video track ensures we're not applying filters on top of filters
        const sourceStream = new MediaStream([sourceVideoTrack]);
        videoElement.srcObject = sourceStream;

        // Wait for video to be ready
        await new Promise((resolve) => {
          videoElement.onloadedmetadata = () => {
            videoElement.play().then(resolve).catch(resolve);
          };
          setTimeout(resolve, 500); // Longer timeout for more reliability
        });

        // Set up the canvas with the same dimensions as the video
        const canvas = document.createElement("canvas");
        canvas.width = videoElement.videoWidth || 640;
        canvas.height = videoElement.videoHeight || 480;
        canvasRef.current = canvas;

        const ctx = canvas.getContext("2d", {
          alpha: false,
          willReadFrequently: true,
        });
        ctxRef.current = ctx;

        // Start the drawing process before creating the new track
        startDrawing(filterType);

        // Create a new stream from the canvas after drawing has started
        const canvasStream = canvas.captureStream(30);

        // Save the stream for cleanup later
        streamCleanupRef.current = canvasStream;

        // Add audio tracks from the original stream
        const audioTracks = localStream.getAudioTracks();
        audioTracks.forEach((track) => {
          canvasStream.addTrack(track);
        });

        // Get the new video track from the canvas stream
        const processedVideoTrack = canvasStream.getVideoTracks()[0];

        if (!processedVideoTrack) {
          throw new Error("Failed to create processed video track");
        }

        // Update local video display FIRST before modifying the actual stream
        if (localVideo && localVideo.current) {
          localVideo.current.srcObject = canvasStream;

          // Wait for the video to start playing
          await new Promise((resolve) => setTimeout(resolve, 200));
        }

        // Now that local display is updated, replace track in the peer connection
        if (pc) {
          const videoSender = pc
            .getSenders()
            .find((sender) => sender.track && sender.track.kind === "video");

          if (videoSender) {
            await videoSender.replaceTrack(processedVideoTrack);
          }
        }

        // Finally update the localStream - we do this last to ensure everything else is working
        const oldVideoTrack = localStream.getVideoTracks()[0];
        if (oldVideoTrack && oldVideoTrack !== sourceVideoTrack) {
          // Only replace if it's not the source track we're using
          localStream.removeTrack(oldVideoTrack);

          // Don't stop the track until we're sure the replacement worked
          localStream.addTrack(processedVideoTrack);

          // Now it's safe to stop the old processed track
          // But be careful not to stop the original camera track
          if (oldVideoTrack !== originalStreamRef.current.getVideoTracks()[0]) {
            oldVideoTrack.stop();
          }
        } else if (!oldVideoTrack) {
          // If there's no track, just add the new one
          localStream.addTrack(processedVideoTrack);
        }

        // Mark processing as active
        isProcessingActiveRef.current = true;
      } catch (error) {
        console.error("Error applying filter:", error);
        // If anything goes wrong, restore the original camera
        alert("There was an error applying the filter. Restoring camera.");
        restoreOriginalCamera();
      }
    };

    // Drawing function that applies the filter effect
    const startDrawing = (filterType) => {
      if (!videoSourceRef.current || !canvasRef.current || !ctxRef.current) {
        console.error("Missing required resources for drawing");
        return;
      }

      const canvas = canvasRef.current;
      const ctx = ctxRef.current;
      const video = videoSourceRef.current;

      // First make sure any previous animation loop is cancelled
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      const drawFrame = () => {
        // Safety check that all resources still exist
        if (!canvas || !ctx || !video || video.readyState < 2) {
          // Keep trying until video is ready
          animationFrameRef.current = requestAnimationFrame(drawFrame);
          return;
        }

        try {
          // Clear canvas first
          ctx.clearRect(0, 0, canvas.width, canvas.height);

          // First draw the original frame
          ctx.filter = "none";
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

          // Then apply the filter
          switch (filterType) {
            case "blur":
              ctx.filter = "blur(8px)";
              ctx.drawImage(canvas, 0, 0, canvas.width, canvas.height);
              break;
            case "grayscale":
              ctx.filter = "grayscale(1)";
              ctx.drawImage(canvas, 0, 0, canvas.width, canvas.height);
              break;
            case "sepia":
              ctx.filter = "sepia(1)";
              ctx.drawImage(canvas, 0, 0, canvas.width, canvas.height);
              break;
            case "invert":
              ctx.filter = "invert(1)";
              ctx.drawImage(canvas, 0, 0, canvas.width, canvas.height);
              break;
            case "brightness":
              ctx.filter = "brightness(1.5)";
              ctx.drawImage(canvas, 0, 0, canvas.width, canvas.height);
              break;
            case "vintage":
              ctx.filter = "sepia(0.5) contrast(1.2) brightness(0.9)";
              ctx.drawImage(canvas, 0, 0, canvas.width, canvas.height);
              break;
            case "cool":
              ctx.filter = "hue-rotate(180deg) saturate(1.3)";
              ctx.drawImage(canvas, 0, 0, canvas.width, canvas.height);
              break;
            case "warm":
              ctx.filter = "sepia(0.3) saturate(1.5) hue-rotate(-30deg)";
              ctx.drawImage(canvas, 0, 0, canvas.width, canvas.height);
              break;
            default:
              // No filter
              break;
          }

          // Reset filter for next frame
          ctx.filter = "none";
        } catch (e) {
          console.error("Error drawing video frame:", e);
        }

        // Continue the loop
        animationFrameRef.current = requestAnimationFrame(drawFrame);
      };

      // Start the animation loop immediately
      animationFrameRef.current = requestAnimationFrame(drawFrame);
    };

    // Restore the original camera without any filters
    const restoreOriginalCamera = async () => {
      try {
        // Clean up existing processing resources
        cleanupResources();

        // Get a fresh camera stream
        const newStream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false,
        });

        const newVideoTrack = newStream.getVideoTracks()[0];

        if (!newVideoTrack) {
          throw new Error("Failed to get new video track from camera");
        }

        // Save this as our new original reference
        const freshOriginalStream = new MediaStream([newVideoTrack]);

        if (localStream) {
          // Get existing audio tracks to preserve
          const audioTracks = localStream.getAudioTracks();

          // Add audio tracks to our reference stream
          audioTracks.forEach((track) => {
            freshOriginalStream.addTrack(track);
          });

          // Update our original stream reference
          originalStreamRef.current = freshOriginalStream;

          // Create a clone for local display and the peer connection
          const refreshedStream = new MediaStream([newVideoTrack]);
          audioTracks.forEach((track) => {
            refreshedStream.addTrack(track);
          });

          // Update local video display first
          if (localVideo && localVideo.current) {
            localVideo.current.srcObject = refreshedStream;

            // Give it a moment to update
            await new Promise((resolve) => setTimeout(resolve, 100));
          }

          // Update the peer connection if it exists
          if (pc) {
            const videoSender = pc
              .getSenders()
              .find((sender) => sender.track && sender.track.kind === "video");

            if (videoSender) {
              await videoSender.replaceTrack(newVideoTrack);
            }
          }

          // Finally, update the local stream by replacing tracks
          const oldVideoTrack = localStream.getVideoTracks()[0];
          if (oldVideoTrack) {
            localStream.removeTrack(oldVideoTrack);
            // Don't stop the original track until we're sure the new one is working
            localStream.addTrack(newVideoTrack);
            // Now it's safe to stop the old track
            oldVideoTrack.stop();
          } else {
            // If there's no track, just add the new one
            localStream.addTrack(newVideoTrack);
          }
        }

        // Mark processing as inactive
        isProcessingActiveRef.current = false;
      } catch (error) {
        console.error("Error restoring camera:", error);
        alert(
          "There was an error restoring the camera. Please refresh the page."
        );
      }
    };

    // Maintain filter even when panel is hidden
    useEffect(() => {
      // We don't want to re-apply the filter when the component visibility changes
      // Only when it's first mounted or if backgroundFilter state changes
    }, [backgroundFilter, localStream]);

    // Don't return null when !isExpanded - just hide the UI
    return (
      <div
        ref={ref}
        className={`${styles.filterPanel} ${!isExpanded ? styles.hidden : ""}`}
      >
        <div className={styles.filterHeader}>
          <h3>Background Filters</h3>
          <button className={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>
        <div className={styles.filterContent}>
          <button
            className={`${styles.filterOption} ${
              backgroundFilter === "none" ? styles.active : ""
            }`}
            onClick={() => applyFilter("none")}
          >
            None
          </button>
          <button
            className={`${styles.filterOption} ${
              backgroundFilter === "blur" ? styles.active : ""
            }`}
            onClick={() => applyFilter("blur")}
          >
            Blur
          </button>
          <button
            className={`${styles.filterOption} ${
              backgroundFilter === "grayscale" ? styles.active : ""
            }`}
            onClick={() => applyFilter("grayscale")}
          >
            B&W
          </button>
          <button
            className={`${styles.filterOption} ${
              backgroundFilter === "sepia" ? styles.active : ""
            }`}
            onClick={() => applyFilter("sepia")}
          >
            Sepia
          </button>
          <button
            className={`${styles.filterOption} ${
              backgroundFilter === "invert" ? styles.active : ""
            }`}
            onClick={() => applyFilter("invert")}
          >
            Invert
          </button>
          <button
            className={`${styles.filterOption} ${
              backgroundFilter === "brightness" ? styles.active : ""
            }`}
            onClick={() => applyFilter("brightness")}
          >
            Bright
          </button>
          <button
            className={`${styles.filterOption} ${
              backgroundFilter === "vintage" ? styles.active : ""
            }`}
            onClick={() => applyFilter("vintage")}
          >
            Vintage
          </button>
          <button
            className={`${styles.filterOption} ${
              backgroundFilter === "cool" ? styles.active : ""
            }`}
            onClick={() => applyFilter("cool")}
          >
            Cool
          </button>
          <button
            className={`${styles.filterOption} ${
              backgroundFilter === "warm" ? styles.active : ""
            }`}
            onClick={() => applyFilter("warm")}
          >
            Warm
          </button>
        </div>
      </div>
    );
  }
);

export default BackgroundFilter;
